/**
 * Вода хозяйства (и контура) механикой экрана «Вода», суженной до своей
 * земли (agroai-6al.37). Полив №N — работа агроцикла:
 *
 * * сверху — KPI цикла (подано от запрошенного с полосой покрытия по поливам,
 *   недоподача, «запросили — не дали», «заявки нет») и график по поливам;
 * * master-detail: слева лента поливов цикла (свежие сверху), справа
 *   выбранный полив — его числа, карта ТОЛЬКО контуров хозяйства с окраской
 *   по состоянию полива, легенда-фильтр, каналы и таблица контуров.
 *
 * Числа — один ответ сервера (`/farms/{id}/water`, `/contours/{id}/water`):
 * карта, легенда, таблица и KPI читают одни строки. Цикл и полив — в адресе
 * (`?wcycle=`, `?watering=`); каждый фильтр виден строкой «Показано» и
 * снимается кнопкой.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Droplets, FileQuestion, Gauge, Layers, Timer } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { request, type QueryParams } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { Card, Grid, Stack } from '../../ui/Card';
import { KpiCard } from '../../ui/KpiCard';
import { Badge } from '../../ui/Badge';
import { DataTable, type Column } from '../../ui/DataTable';
import { DoneSplit } from '../../ui/DoneSplit';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError, failed } from '../../ui/LoadError';
import { ActiveFilter, type ActiveFilterItem } from '../../ui/ActiveFilter';
import { ChartContainer, ChartLegend, ChartTooltipContent, niceTicks, type ChartConfig } from '../../ui/chart/Chart';
import { AgroMap } from '../../map/MapWidget';
import { defineMessages } from '../../../i18n/define';
import { useT, UNIT_LABELS } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import { dateShort, ha, haUnit, m3, m3Compact, num, pct, windowLabel } from '../../lib/format';
import { ChannelList, CoverageStrip, type CoveragePoint } from '../WaterCharts';
import { wateringTitle } from '../waterSummary';
import { FarmIrrigations } from '../water/FarmIrrigations';
import {
  channelRowsOf,
  isEvents,
  pickWatering,
  stateCounts,
  stateOf,
  vol,
  waterTotals,
  wateringKey,
  wateringLayer,
  wateringPhase,
  waterStateMeta,
  WATER_STATE_ORDER,
  type ContourWaterState,
  type FarmWater,
  type Watering,
  type WateringContour,
} from './water';
import { LegendFilter, toggled, usePatchParams } from './parts';
import { WateringSkeleton } from './CabinetSkeleton';
import '../water.css';


const m = defineMessages({
  contoursCount: { id: 'contour.watering.count.contour', defaultMessage: '{count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}', description: 'Число контуров в подписи под KPI и в ленте поливов' },
  wateringsCount: { id: 'contour.watering.count.watering', defaultMessage: '{count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}}', description: 'Число поливов (полив №1, №2…) в подписях KPI и бейдже карточки' },
  daysCount: { id: 'contour.watering.count.day', defaultMessage: '{count, plural, one {# день} few {# дня} many {# дней} other {# дня}}', description: 'Число дней до конца окна полива, в бейдже «идёт, осталось …»' },
  contoursUnit: { id: 'contour.watering.unit.contour', defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}', description: 'Слово после числа в легенде состояний полива и в знаменателе таблицы: «5 контуров»; без числа' },
  requestsUnit: { id: 'contour.watering.unit.request', defaultMessage: '{count, plural, one {заявка} few {заявки} many {заявок} other {заявки}}', description: 'Единица рядом с числом в KPI «Запросили — не дали»: «3 заявки»; без числа' },
  obligationsUnit: { id: 'contour.watering.unit.obligation', defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}', description: 'Слово-знаменатель под полосой выполнения полива: «5 из 12 работ на полях»; работа полива, поручённая контуру; без числа' },
  chartRequested: { id: 'contour.watering.chart.requested', defaultMessage: 'Запрошено', description: 'Подпись столбика и легенды графика поливов: запрошенный объём воды' },
  chartDelivered: { id: 'contour.watering.chart.delivered', defaultMessage: 'Подано', description: 'Подпись столбика и легенды графика поливов: поданный объём воды' },
  phaseActiveLeft: { id: 'contour.watering.phase.activeLeft', defaultMessage: 'идёт, осталось {days}', description: 'Бейдж полива: окно идёт; {days} — число дней со словом («3 дня»); коротко' },
  phaseActive: { id: 'contour.watering.phase.active', defaultMessage: 'идёт', description: 'Бейдж полива и метка у агроцикла: окно идёт; коротко' },
  phaseDone: { id: 'contour.watering.phase.done', defaultMessage: 'окно закрыто', description: 'Бейдж полива: окно закрылось; коротко' },
  phaseUpcoming: { id: 'contour.watering.phase.upcoming', defaultMessage: 'впереди', description: 'Бейдж полива: окно впереди; коротко' },
  phaseUnset: { id: 'contour.watering.phase.unset', defaultMessage: 'окно не назначено', description: 'Бейдж полива: окно не назначено; коротко' },
  cycleFallback: { id: 'contour.watering.cycleFallback', defaultMessage: 'агроцикл', description: 'Подстановка вместо названия агроцикла, когда оно неизвестно (в подписи «агроцикл …»)' },
  filterCycle: { id: 'contour.watering.filter.cycle', defaultMessage: 'агроцикл {name}', description: 'Чип активного фильтра воды: выбранный агроцикл; {name} — название («Хлопок-2026»)' },
  filterIdle: { id: 'contour.watering.filter.idle', defaultMessage: 'агроцикл {name}, полив по дате {date}', description: 'Подпись фильтра воды, когда ничего не выбрано; {name} — название агроцикла, {date} — дата среза' },
  filterChannel: { id: 'contour.watering.filter.channel', defaultMessage: 'канал → {label}', description: 'Чип активного фильтра: выбран канал полива; {label} — название канала' },
  noCyclesTitle: { id: 'contour.watering.noCycles.title', defaultMessage: 'Агроциклов нет', description: 'Заголовок пустого состояния вкладки «Вода»: у хозяйства или контура нет агроциклов' },
  noCyclesReason: { id: 'contour.watering.noCycles.reason', defaultMessage: 'без агроцикла поливов нет: полив — работа агроцикла культуры', description: 'Пояснение к пустому состоянию вкладки «Вода»: нет агроциклов' },
  cycleAria: { id: 'contour.watering.cycle.aria', defaultMessage: 'Агроцикл', description: 'aria-label группы переключателей агроцикла на вкладке «Вода»' },
  loadWhat: { id: 'contour.watering.loadWhat', defaultMessage: 'воду по поливам', description: 'Что не загрузилось (винительный падеж) в сообщении «Не удалось загрузить …» на вкладке «Вода»' },
  noWateringsTitle: { id: 'contour.watering.none.title', defaultMessage: 'Поливов в агроцикле «{name}» нет', description: 'Заголовок пустого состояния: в выбранном агроцикле нет поливов; {name} — название агроцикла' },
  noWateringsFarm: { id: 'contour.watering.none.farm', defaultMessage: 'у хозяйства нет работ полива в этом агроцикле — у зерновых поливы часто не назначаются; выберите другой агроцикл выше', description: 'Пояснение к пустому состоянию воды в кабинете хозяйства' },
  noWateringsContour: { id: 'contour.watering.none.contour', defaultMessage: 'на контуре нет работ полива в этом агроцикле', description: 'Пояснение к пустому состоянию воды в карточке контура' },
  wateringsTitle: { id: 'contour.watering.list.title', defaultMessage: 'Поливы агроцикла', description: 'Заголовок карточки с лентой поливов выбранного агроцикла и подробностями выбранного полива' },
  chartTitle: { id: 'contour.watering.chart.title', defaultMessage: 'Запрошено и подано по поливам', description: 'Заголовок карточки с графиком запрошенной и поданной воды по поливам агроцикла' },
  chartAria: { id: 'contour.watering.chart.aria', defaultMessage: 'Запрошено и подано по поливам агроцикла', description: 'Подпись графика для чтения с экрана' },
  chartNote: { id: 'contour.watering.chart.note', defaultMessage: 'Полив без столбиков — по нему не было ни одной подачи: смотрите в ленте, «запросили — не дали» это или «заявки нет».', description: 'Пояснение под графиком поливов' },
  kpiRunning: { id: 'contour.watering.kpi.running', defaultMessage: 'Идёт {title}', description: 'Подпись KPI, когда полив идёт; {title} — название полива строчными («полив №2»)' },
  kpiNotRunning: { id: 'contour.watering.kpi.notRunning', defaultMessage: 'Полив сейчас не идёт', description: 'Подпись KPI, когда ни один полив не идёт' },
  kpiNone: { id: 'contour.watering.kpi.none', defaultMessage: 'нет', description: 'Значение KPI, когда поливов нет ни идущего, ни закрытого' },
  kpiWindow: { id: 'contour.watering.kpi.window', defaultMessage: 'окно {window}', description: 'Мелкая подпись под KPI идущего полива; {window} — окно дат' },
  kpiLastClosed: { id: 'contour.watering.kpi.lastClosed', defaultMessage: 'последнее окно закрылось {date}', description: 'Мелкая подпись под KPI, когда полив не идёт; {date} — короткая дата' },
  kpiAhead: { id: 'contour.watering.kpi.ahead', defaultMessage: 'окна поливов ещё впереди', description: 'Мелкая подпись под KPI, когда все окна поливов впереди' },
  kpiCoverage: { id: 'contour.watering.kpi.coverage', defaultMessage: 'Подано от запрошенного', description: 'Подпись KPI: доля поданной воды от запрошенной по закрытым окнам' },
  kpiNoRequests: { id: 'contour.watering.kpi.noRequests', defaultMessage: 'нет заявок', description: 'Значение KPI «Подано от запрошенного», когда заявок не было' },
  kpiCoverageNote: { id: 'contour.watering.kpi.coverageNote', defaultMessage: '{delivered} из {requested} · по {count} с закрытым окном · {cycle}', description: 'Мелкая подпись под KPI покрытия; {delivered}/{requested} — объёмы воды с единицей, {count} — число поливов со словом («3 полива»), {cycle} — название агроцикла' },
  kpiShort: { id: 'contour.watering.kpi.short', defaultMessage: 'Недоподача', description: 'Подпись KPI: сколько воды не подали из запрошенного' },
  kpiNoData: { id: 'contour.watering.kpi.noData', defaultMessage: 'нет данных', description: 'Значение KPI или показателя, когда данных нет' },
  kpiShortNote: { id: 'contour.watering.kpi.shortNote', defaultMessage: 'сумма по контурам: запрошено − подано', description: 'Мелкая подпись под KPI «Недоподача»' },
  kpiPending: { id: 'contour.watering.kpi.pending', defaultMessage: 'Запросили — не дали', description: 'Подпись KPI: заявки на воду, по которым не было ни одной подачи' },
  kpiPendingNote: { id: 'contour.watering.kpi.pendingNote', defaultMessage: '{contours}, {volume} без подачи; «заявки нет» — {noRequest, plural, one {# контур в поливе} few {# контура в поливах} many {# контуров в поливах} other {# контура в поливах}}', description: 'Мелкая подпись под KPI «Запросили — не дали»; {contours} — число контуров со словом, {volume} — объём воды с единицей, {noRequest} — сколько контуров в поливах без заявки' },
  kpiPendingNoneNote: { id: 'contour.watering.kpi.pendingNoneNote', defaultMessage: 'заявок без подачи нет; «заявки нет» — {noRequest, plural, one {# контур в поливе} few {# контура в поливах} many {# контуров в поливах} other {# контура в поливах}} с закрытым окном', description: 'Мелкая подпись под KPI «Запросили — не дали», когда таких заявок нет; {noRequest} — сколько контуров в поливах без заявки при закрытом окне' },
  feedAria: { id: 'contour.watering.feed.aria', defaultMessage: 'Поливы агроцикла, свежие сверху', description: 'aria-label ленты поливов (порядок: свежие сверху)' },
  feedDelivered: { id: 'contour.watering.feed.deliveredAria', defaultMessage: 'Подано {delivered} из {requested}', description: 'Описание полосы покрытия полива для чтения с экрана; {delivered}/{requested} — объёмы воды с единицей' },
  feedCoverage: { id: 'contour.watering.feed.coverage', defaultMessage: 'покрытие {value}', description: 'Мелкая подпись в карточке полива: доля поданной воды; {value} — проценты' },
  feedUpcoming: { id: 'contour.watering.feed.upcoming', defaultMessage: 'Окно ещё не открылось — подач и заявок по нему пока нет.', description: 'Текст в карточке полива, чьё окно впереди' },
  feedPending: { id: 'contour.watering.feed.pending', defaultMessage: 'Запросили — не дали: {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}.', description: 'Строка в карточке полива без объёмов: контуры с заявкой без подачи' },
  feedNoRequest: { id: 'contour.watering.feed.noRequest', defaultMessage: 'Заявки нет: {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}.', description: 'Строка в карточке полива без объёмов: контуры, закрывшие окно без заявки' },
  feedInWindow: { id: 'contour.watering.feed.inWindow', defaultMessage: 'Окно идёт: {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} ещё не срок.', description: 'Строка в карточке полива без объёмов: контуры, у которых окно идёт и срок не вышел' },
  farmFallback: { id: 'contour.watering.detail.farmFallback', defaultMessage: 'хозяйство №{id}', description: 'Подстановка вместо названия хозяйства в шапке полива; {id} — числовой код' },
  colContour: { id: 'contour.watering.table.col.contour', defaultMessage: 'Контур', description: 'Колонка таблицы контуров полива: номер контура' },
  contourNo: { id: 'contour.watering.table.contourNo', defaultMessage: '№ {no}', description: 'Номер контура в ячейке таблицы; {no} — кадастровый номер' },
  colState: { id: 'contour.watering.table.col.state', defaultMessage: 'Состояние', description: 'Колонка таблицы контуров полива: состояние полива на контуре' },
  unplanned: { id: 'contour.watering.table.unplanned', defaultMessage: 'вне плана', description: 'Пометка у состояния в таблице: полив вне плана агроцикла (событие полива, не работа плана)' },
  colRequested: { id: 'contour.watering.table.col.requested', defaultMessage: 'Запрошено', description: 'Колонка таблицы контуров полива: запрошенный объём, м³; числовая колонка' },
  colDelivered: { id: 'contour.watering.table.col.delivered', defaultMessage: 'Подано', description: 'Колонка таблицы контуров полива: поданный объём, м³; числовая колонка' },
  colCoverage: { id: 'contour.watering.table.col.coverage', defaultMessage: 'Покрытие', description: 'Колонка таблицы контуров полива: доля поданного от запрошенного, %; числовая колонка' },
  colShort: { id: 'contour.watering.table.col.short', defaultMessage: 'Не подано', description: 'Колонка таблицы контуров полива: недоподача, м³; числовая колонка' },
  colChannel: { id: 'contour.watering.table.col.channel', defaultMessage: 'Канал', description: 'Колонка таблицы контуров полива: канал подачи воды' },
  pendingCell: { id: 'contour.watering.table.pendingCell', defaultMessage: '{value} без подачи', description: 'Ячейка «Запрошено», когда по контуру есть заявка без подачи; {value} — объём (число, м³)' },
  statRequested: { id: 'contour.watering.stat.requested', defaultMessage: 'Запрошено', description: 'Подпись показателя выбранного полива: запрошенный объём воды' },
  statRequestedNote: { id: 'contour.watering.stat.requestedNote', defaultMessage: 'позиции, получившие подачу полива', description: 'Пояснение показателя «Запрошено»: учтены только позиции заявок, по которым была подача' },
  statDelivered: { id: 'contour.watering.stat.delivered', defaultMessage: 'Подано', description: 'Подпись показателя выбранного полива: поданный объём воды' },
  statDeliveredNote: { id: 'contour.watering.stat.deliveredNote', defaultMessage: 'покрытие {value}', description: 'Пояснение показателя «Подано»; {value} — проценты' },
  statShort: { id: 'contour.watering.stat.short', defaultMessage: 'Не подано', description: 'Подпись показателя выбранного полива: недоподача' },
  statShortNote: { id: 'contour.watering.stat.shortNote', defaultMessage: 'сумма по контурам', description: 'Пояснение показателя «Не подано»' },
  statContours: { id: 'contour.watering.stat.contours', defaultMessage: 'Контуров с подачей', description: 'Подпись показателя выбранного полива: сколько контуров получили воду' },
  statContoursOf: { id: 'contour.watering.stat.contoursOf', defaultMessage: 'из {total}', description: 'Мелкое «из N» рядом с числом контуров с подачей; {total} — всего контуров полива' },
  statContoursNote: { id: 'contour.watering.stat.contoursNote', defaultMessage: 'запросили — не дали {pending} · заявки нет {noRequest}', description: 'Пояснение показателя «Контуров с подачей»; {pending} — контуры с заявкой без подачи, {noRequest} — контуры без заявки; числа отформатированы' },
  windowNote: { id: 'contour.watering.detail.windowNote', defaultMessage: 'окно идёт: нет отчёта — значит «ещё не срок»', description: 'Пояснение под полосой выполнения идущего полива: отсутствие отчёта пока не означает просрочку' },
  mapCaptionFarm: { id: 'contour.watering.map.captionFarm', defaultMessage: '{title}: только контуры хозяйства', description: 'Подпись под картой полива в кабинете хозяйства; {title} — название полива' },
  mapCaptionContour: { id: 'contour.watering.map.captionContour', defaultMessage: '{title}: этот контур', description: 'Подпись под картой полива в карточке контура; {title} — название полива' },
  cardState: { id: 'contour.watering.map.cardState', defaultMessage: '{state}: запрошено {requested}, подано {delivered}', description: 'Строка во всплывающей карточке контура на карте полива; {state} — состояние полива, {requested}/{delivered} — объёмы воды с единицей' },
  cardStatePending: { id: 'contour.watering.map.cardStatePending', defaultMessage: '{state}: запрошено {requested}, подано {delivered}; заявка без подачи {pending}', description: 'То же, когда есть заявка без подачи; {pending} — объём заявки без подачи с единицей' },
  cardWater: { id: 'contour.watering.map.cardWater', defaultMessage: 'Вода контура', description: 'Название поля во всплывающей карточке контура на карте полива' },
  cardAllWaterings: { id: 'contour.watering.map.cardAllWaterings', defaultMessage: 'Все поливы контура →', description: 'Ссылка во всплывающей карточке контура на карте полива: открыть вкладку «Вода» контура' },
  legendLabel: { id: 'contour.watering.legend.label', defaultMessage: 'Состояние полива на контурах', description: 'aria-label легенды-фильтра состояний полива рядом с картой' },
  legendFoot: { id: 'contour.watering.legend.foot', defaultMessage: 'Нажатие оставляет на карте и в таблице только этот статус. «Запросили — не дали» — у контура есть заявка без единой подачи; «заявки нет» — окно закрылось без заявки и подачи.', description: 'Пояснение внизу легенды состояний полива' },
  channelsTitle: { id: 'contour.watering.channels.title', defaultMessage: 'Каналы этого полива', description: 'Подзаголовок списка каналов выбранного полива' },
  tableTitle: { id: 'contour.watering.table.title', defaultMessage: 'Контуры этого полива', description: 'Подзаголовок таблицы контуров выбранного полива' },
  tableEmptyTitle: { id: 'contour.watering.table.empty.title', defaultMessage: 'Контуров нет', description: 'Заголовок пустой таблицы контуров полива' },
  tableEmptyReason: { id: 'contour.watering.table.empty.reason', defaultMessage: 'под выбранный фильтр контуров нет — снимите его выше', description: 'Пояснение к пустой таблице контуров полива при включённом фильтре' },
  evKpiDelivered: { id: 'contour.watering.ev.kpi.delivered', defaultMessage: 'Подано воды', description: 'Подпись KPI вкладки «Вода» (события файла ирригации): объём воды по файлу за цикл' },
  evKpiDeliveredNote: { id: 'contour.watering.ev.kpi.deliveredNote', defaultMessage: '{events} · {through} · {cycle}', description: 'Мелкая подпись под KPI «Подано воды»; {events} — число событий со словом, {through} — «данные ирригации по ДД.ММ», {cycle} — название агроцикла' },
  evKpiDeliveredNoFile: { id: 'contour.watering.ev.kpi.deliveredNoFile', defaultMessage: 'файла ирригации по циклу ещё нет · {cycle}', description: 'Мелкая подпись под KPI «Подано воды», когда опубликованных файлов нет; {cycle} — название агроцикла' },
  evThrough: { id: 'contour.watering.ev.through', defaultMessage: 'данные ирригации по', description: 'Начало подписи свежести данных перед датой: «данные ирригации по 19.09»' },
  evEventsCount: { id: 'contour.watering.ev.eventsCount', defaultMessage: '{count, plural, one {# событие} few {# события} many {# событий} other {# события}}', description: 'Число событий файла ирригации со словом' },
  evKpiLand: { id: 'contour.watering.ev.kpi.land', defaultMessage: 'Полито земли', description: 'Подпись KPI вкладки «Вода»: гектары земли, где был полив по файлу ирригации' },
  evKpiLandNote: { id: 'contour.watering.ev.kpi.landNote', defaultMessage: 'каждый гектар один раз · {cycle}', description: 'Мелкая подпись под KPI «Полито земли»; {cycle} — название агроцикла' },
  evKpiNoDelivery: { id: 'contour.watering.ev.kpi.noDelivery', defaultMessage: 'Без подачи', description: 'Подпись KPI вкладки «Вода»: полив плана, окно которого закрылось без события в файле ирригации' },
  evKpiNoDeliveryUnit: { id: 'contour.watering.ev.kpi.noDeliveryUnit', defaultMessage: '{count, plural, one {пара «поле × полив»} few {пары «поле × полив»} many {пар «поле × полив»} other {пары «поле × полив»}}', description: 'Единица рядом с числом KPI «Без подачи»: поле × полив плана' },
  evKpiNoDeliveryNote: { id: 'contour.watering.ev.kpi.noDeliveryNote', defaultMessage: 'окно закрылось, события в файле нет; ждём подачи — {waiting}', description: 'Мелкая подпись под KPI «Без подачи»; {waiting} — число пар «поле × полив», у которых окно ещё идёт (число строкой)' },
  evChartTitle: { id: 'contour.watering.ev.chart.title', defaultMessage: 'Подано по поливам', description: 'Заголовок карточки с графиком воды по поливам агроцикла (события файла ирригации)' },
  evChartAria: { id: 'contour.watering.ev.chart.aria', defaultMessage: 'Подано воды по поливам агроцикла по файлу ирригации', description: 'Подпись графика воды по поливам для чтения с экрана' },
  evChartNote: { id: 'contour.watering.ev.chart.note', defaultMessage: 'Полив без столбика — по нему в файле ирригации нет событий: смотрите в ленте, «без подачи» это или «ждём».', description: 'Пояснение под графиком поливов (события файла ирригации)' },
  evFeedLine: { id: 'contour.watering.ev.feed.line', defaultMessage: '{volume} · {land} · {events}', description: 'Строка факта в карточке полива; {volume} — объём воды, {land} — политая земля, {events} — число событий со словом' },
  evFeedNoDelivery: { id: 'contour.watering.ev.feed.noDelivery', defaultMessage: 'Без подачи: {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}.', description: 'Строка в карточке полива: контуры, у которых окно закрылось без события в файле' },
  evFeedWaiting: { id: 'contour.watering.ev.feed.waiting', defaultMessage: 'Ждём подачи: {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}.', description: 'Строка в карточке полива: контуры, у которых окно идёт и события ещё нет' },
  evStatDelivered: { id: 'contour.watering.ev.stat.delivered', defaultMessage: 'Подано', description: 'Подпись показателя выбранного полива: объём воды по файлу ирригации' },
  evStatDeliveredNote: { id: 'contour.watering.ev.stat.deliveredNote', defaultMessage: 'по файлу ирригации', description: 'Пояснение показателя «Подано» выбранного полива' },
  evStatLand: { id: 'contour.watering.ev.stat.land', defaultMessage: 'Полито земли', description: 'Подпись показателя выбранного полива: гектары земли с поливом' },
  evStatLandNote: { id: 'contour.watering.ev.stat.landNote', defaultMessage: 'площадь события, иначе площадь полива контура', description: 'Пояснение показателя «Полито земли»' },
  evStatEvents: { id: 'contour.watering.ev.stat.events', defaultMessage: 'События файла', description: 'Подпись показателя выбранного полива: сколько событий файла ирригации закрыли полив' },
  evStatEventsNote: { id: 'contour.watering.ev.stat.eventsNote', defaultMessage: 'каналов: {channels}', description: 'Пояснение показателя «События файла»; {channels} — число каналов, число строкой' },
  evStatContoursNote: { id: 'contour.watering.ev.stat.contoursNote', defaultMessage: 'без подачи {noDelivery} · ждём {waiting}', description: 'Пояснение показателя «Контуров с подачей»; {noDelivery} — контуры без подачи, {waiting} — контуры в ожидании; числа строкой' },
  evColLand: { id: 'contour.watering.ev.col.land', defaultMessage: 'Полито', description: 'Колонка таблицы контуров полива: политая земля, га; числовая колонка' },
  evColEvents: { id: 'contour.watering.ev.col.events', defaultMessage: 'Событий', description: 'Колонка таблицы контуров полива: число событий файла; числовая колонка' },
  evCardState: { id: 'contour.watering.ev.map.cardState', defaultMessage: '{state}: подано {delivered}, полито {land}', description: 'Строка во всплывающей карточке контура на карте полива (события файла); {state} — состояние, {delivered} — объём воды с единицей, {land} — земля с единицей' },
  evLegendFoot: { id: 'contour.watering.ev.legend.foot', defaultMessage: 'Нажатие оставляет на карте и в таблице только этот статус. «Без подачи» — окно закрылось, события в файле ирригации нет (файл мог опоздать); «ждём подачи» — окно идёт.', description: 'Пояснение внизу легенды состояний полива (события файла ирригации)' },
});

/** Конфиг графика строится при рендере: подписи и единица зависят от языка. */
const chartConfig = (): ChartConfig => {
  const unit = tr(UNIT_LABELS.m3);
  return {
    requested: { label: tr(m.chartRequested), token: '--data-axis', forms: [unit, unit, unit] },
    delivered: { label: tr(m.chartDelivered), token: '--data-water', forms: [unit, unit, unit] },
  };
};

/** Конфиг графика по событиям файла: одна серия — «подано», заявок нет. */
const eventsChartConfig = (): ChartConfig => {
  const unit = tr(UNIT_LABELS.m3);
  return { delivered: { label: tr(m.chartDelivered), token: '--data-water', forms: [unit, unit, unit] } };
};

export interface WaterCycleChoice {
  cycle: string;
  name: string;
  active: boolean;
}

export interface WateringViewProps {
  scope: { kind: 'farm' | 'contour'; id: number };
  cycles: WaterCycleChoice[];
  /** Цикл по умолчанию — когда в адресе его нет. */
  defaultCycle: string | null;
  asOf: string;
  params: QueryParams;
  paramsKey: string;
  linkSearch: (extra?: Record<string, string | number>) => string;
  /** Раздел под разбором поливов: недели воды, заявки (если они есть). Функция — когда нужен цикл и ветка. */
  after?: ReactNode | ((info: { cycle: string | null; events: boolean }) => ReactNode);
  /** Циклы ещё читаются: список пуст не потому, что циклов нет. */
  cyclesLoading?: boolean;
}

function phaseBadge(w: Watering, day: string): { text: string; tone: 'aqua' | 'neutral' | 'outline' } {
  const phase = wateringPhase(w, day);
  if (phase === 'active') {
    const left = w.window_to ? Math.round((Date.parse(w.window_to) - Date.parse(day)) / 86_400_000) : null;
    return { text: left !== null && left >= 0 ? tr(m.phaseActiveLeft, { days: tr(m.daysCount, { count: left }) }) : tr(m.phaseActive), tone: 'aqua' };
  }
  if (phase === 'done') return { text: tr(m.phaseDone), tone: 'neutral' };
  if (phase === 'upcoming') return { text: tr(m.phaseUpcoming), tone: 'outline' };
  return { text: tr(m.phaseUnset), tone: 'outline' };
}

export function WateringView(props: WateringViewProps) {
  const t = useT();
  const { scope, cycles, asOf, params, paramsKey, linkSearch } = props;
  const [search] = useSearchParams();
  const patch = usePatchParams();
  const askedCycle = search.get('wcycle');
  const cycle = cycles.find((c) => c.cycle === askedCycle)?.cycle ?? props.defaultCycle ?? cycles[0]?.cycle ?? null;
  const base = scope.kind === 'farm' ? `/farms/${scope.id}/water` : `/contours/${scope.id}/water`;
  const res = useRequest(
    () => (cycle ? request<FarmWater>(base, { ...params, cycle }) : Promise.resolve(null)),
    [base, cycle, paramsKey],
  );
  const list = useMemo(() => res.data?.waterings ?? [], [res.data]);
  const watering = pickWatering(list, search.get('watering'), asOf);
  const totals = useMemo(() => waterTotals(res.data), [res.data]);
  const cycleName = cycles.find((c) => c.cycle === cycle)?.name ?? cycle ?? t(m.cycleFallback);

  const filters: ActiveFilterItem[] = [];
  if (askedCycle && askedCycle !== props.defaultCycle) {
    filters.push({ key: 'cycle', text: t(m.filterCycle, { name: cycleName }), onClear: () => patch({ wcycle: null, watering: null }) });
  }
  if (search.get('watering') && watering) {
    filters.push({ key: 'watering', text: wateringTitle(watering.name).title, onClear: () => patch({ watering: null }) });
  }

  if (cycles.length === 0 && props.cyclesLoading) return <WateringSkeleton withFilter />;
  if (cycles.length === 0) {
    return <EmptyState title={t(m.noCyclesTitle)} reason={t(m.noCyclesReason)} />;
  }

  return (
    <Stack>
      {/* Поливы по файлу ирригации — события, а не заявки: факт полива приходит только из файла. */}
      {scope.kind === 'farm' ? <FarmIrrigations actorId={scope.id} cycle={cycle} /> : null}
      <div className="cab-filterrow" data-guide="water-filters">
        <div className="cab-chips" role="group" aria-label={t(m.cycleAria)}>
          {cycles.map((c) => (
            <button key={c.cycle} type="button" className="cab-pick" aria-pressed={c.cycle === cycle} onClick={() => patch({ wcycle: c.cycle === props.defaultCycle ? null : c.cycle, watering: null })}>
              {c.name}
              {c.active ? <small>{t(m.phaseActive)}</small> : null}
            </button>
          ))}
        </div>
        <ActiveFilter
          items={filters}
          onReset={() => patch({ wcycle: null, watering: null })}
          idle={t(m.filterIdle, { name: cycleName, date: dateShort(asOf) })}
        />
      </div>

      {failed(res) ? (
        <LoadError what={t(m.loadWhat)} error={res.error} onRetry={res.reload} />
      ) : !res.data ? (
        <WateringSkeleton />
      ) : list.length === 0 ? (
        <EmptyState
          icon={Droplets}
          title={t(m.noWateringsTitle, { name: cycleName })}
          reason={scope.kind === 'farm' ? t(m.noWateringsFarm) : t(m.noWateringsContour)}
        />
      ) : (
        <>
          <div data-guide="water-kpi">
            <WaterKpis list={list} totals={totals} asOf={asOf} selected={watering} cycleName={cycleName} pending={res.data} events={isEvents(res.data)} />
          </div>
          <div data-guide="water-waterings">
            <Card size="lg" title={t(m.wateringsTitle)} aside={<Badge tone="neutral">{t(m.wateringsCount, { count: list.length })}</Badge>}>
              <div className="wv-split">
                <div className="wv-split__rail" data-guide="water-feed">
                  <Feed list={list} day={asOf} selected={watering} events={isEvents(res.data)} onPick={(w) => patch({ watering: wateringKey(w) })} />
                </div>
                <div className="wv-split__main">
                  {watering ? (
                    <Detail key={wateringKey(watering)} w={watering} day={asOf} scope={scope} linkSearch={linkSearch} multiFarm={new Set(list.map((x) => x.actor.id)).size > 1} events={isEvents(res.data)} />
                  ) : null}
                </div>
              </div>
            </Card>
          </div>
          <div data-guide="water-chart">
            <Card title={isEvents(res.data) ? t(m.evChartTitle) : t(m.chartTitle)} aside={cycleName}>
              <WateringChart list={list} events={isEvents(res.data)} />
              <p className="cab-why">{res.data.note}</p>
            </Card>
          </div>
        </>
      )}
      {typeof props.after === 'function' ? props.after({ cycle, events: isEvents(res.data) }) : props.after}
    </Stack>
  );
}

/* --------------------------------------------------------------- KPI */

function WaterKpis({ list, totals, asOf, selected, cycleName, pending, events }: { list: Watering[]; totals: ReturnType<typeof waterTotals>; asOf: string; selected: Watering | null; cycleName: string; pending: FarmWater; events: boolean }) {
  const t = useT();
  const running = list.find((w) => wateringPhase(w, asOf) === 'active') ?? null;
  const last = [...list].reverse().find((w) => wateringPhase(w, asOf) === 'done') ?? null;
  const points: CoveragePoint[] = list.map((w) => ({
    key: wateringKey(w),
    label: wateringTitle(w.name).title,
    coveragePct: w.coverage_pct ?? null,
    current: selected !== null && wateringKey(w) === wateringKey(selected),
  }));
  const pendingM3 = vol(pending.pending_requested_m3);
  if (events) {
    const waiting = list.reduce((sum, w) => sum + stateCounts(w).in_window, 0);
    return (
      <Grid cols={4}>
        <KpiCard
          label={running ? t(m.kpiRunning, { title: wateringTitle(running.name).title.toLowerCase() }) : t(m.kpiNotRunning)}
          icon={Timer}
          value={running ? wateringTitle(running.name).title : last ? wateringTitle(last.name).title : t(m.kpiNone)}
          note={running ? t(m.kpiWindow, { window: windowLabel(running.window_from, running.window_to) }) : last ? t(m.kpiLastClosed, { date: dateShort(last.window_to) }) : t(m.kpiAhead)}
        />
        <KpiCard
          label={t(m.evKpiDelivered)}
          icon={Gauge}
          value={totals.deliveredM3 === null ? t(m.kpiNoData) : m3Compact(totals.deliveredM3)}
          note={
            pending.data_through
              ? t(m.evKpiDeliveredNote, { events: t(m.evEventsCount, { count: totals.events }), through: `${t(m.evThrough)} ${dateShort(pending.data_through)}`, cycle: cycleName })
              : t(m.evKpiDeliveredNoFile, { cycle: cycleName })
          }
        />
        <KpiCard
          label={t(m.evKpiLand)}
          icon={Layers}
          value={totals.areaHa === null ? t(m.kpiNoData) : ha(totals.areaHa)}
          unit={totals.areaHa === null ? undefined : t(UNIT_LABELS.ha)}
          note={t(m.evKpiLandNote, { cycle: cycleName })}
        />
        <KpiCard
          label={t(m.evKpiNoDelivery)}
          icon={FileQuestion}
          value={num(totals.noDelivery)}
          unit={t(m.evKpiNoDeliveryUnit, { count: totals.noDelivery })}
          tone={totals.noDelivery > 0 ? 'warning' : undefined}
          note={t(m.evKpiNoDeliveryNote, { waiting: num(waiting) })}
        />
      </Grid>
    );
  }
  return (
    <Grid cols={4}>
      <KpiCard
        label={running ? t(m.kpiRunning, { title: wateringTitle(running.name).title.toLowerCase() }) : t(m.kpiNotRunning)}
        icon={Timer}
        value={running ? wateringTitle(running.name).title : last ? wateringTitle(last.name).title : t(m.kpiNone)}
        note={running ? t(m.kpiWindow, { window: windowLabel(running.window_from, running.window_to) }) : last ? t(m.kpiLastClosed, { date: dateShort(last.window_to) }) : t(m.kpiAhead)}
      />
      <KpiCard
        label={t(m.kpiCoverage)}
        icon={Gauge}
        value={totals.coveragePct === null ? t(m.kpiNoRequests) : pct(totals.coveragePct, 1)}
        note={
          <span className="wv-kpinote">
            <CoverageStrip points={points} />
            <span>
              {t(m.kpiCoverageNote, { delivered: m3Compact(totals.deliveredM3), requested: m3Compact(totals.requestedM3), count: totals.closedWindows, cycle: cycleName })}
            </span>
          </span>
        }
      />
      <KpiCard
        label={t(m.kpiShort)}
        icon={Droplets}
        value={totals.shortM3 === null ? t(m.kpiNoData) : m3Compact(totals.shortM3)}
        tone={(totals.shortM3 ?? 0) > 0 ? 'warning' : undefined}
        note={t(m.kpiShortNote)}
      />
      <KpiCard
        label={t(m.kpiPending)}
        icon={FileQuestion}
        value={num(pending.pending_lines)}
        unit={t(m.requestsUnit, { count: pending.pending_lines })}
        tone={pending.pending_lines > 0 ? 'warning' : undefined}
        note={
          pending.pending_lines > 0
            ? t(m.kpiPendingNote, { contours: t(m.contoursCount, { count: pending.pending_contours }), volume: m3Compact(pendingM3), noRequest: totals.noRequest })
            : t(m.kpiPendingNoneNote, { noRequest: totals.noRequest })
        }
      />
    </Grid>
  );
}

/* -------------------------------------------------------------- лента */

function Feed({ list, day, selected, onPick, events }: { list: Watering[]; day: string; selected: Watering | null; onPick: (w: Watering) => void; events: boolean }) {
  const t = useT();
  const feed = [...list].reverse();
  return (
    <ol className="wv-feed" aria-label={t(m.feedAria)}>
      {feed.map((w) => {
        const key = wateringKey(w);
        const phase = wateringPhase(w, day);
        const badge = phaseBadge(w, day);
        const name = wateringTitle(w.name);
        const req = vol(w.requested_m3);
        const del = vol(w.delivered_m3);
        const ratio = w.coverage_pct ?? null;
        const counts = stateCounts(w);
        const on = selected !== null && wateringKey(selected) === key;
        return (
          <li key={key} className="wv-feed__item">
            <span className={`wfeed__dot wfeed__dot--${phase === 'active' ? 'active' : phase === 'done' ? 'done' : 'upcoming'}`} aria-hidden="true" />
            <div className={`wv-card ${on ? 'is-on' : ''}`} onClick={() => onPick(w)}>
              <button type="button" className="wv-card__pick focus-ring" aria-pressed={on} onClick={(e) => { e.stopPropagation(); onPick(w); }}>
                <span className="wv-card__name">{name.title}</span>
                <Badge tone={badge.tone}>{badge.text}</Badge>
              </button>
              <div className="wv-card__win">
                {windowLabel(w.window_from, w.window_to)}
                {name.note ? ` · ${name.note}` : ''}
              </div>
              {events ? (
                phase === 'upcoming' ? (
                  <p className="t-caption">{t(m.feedUpcoming)}</p>
                ) : (
                  <>
                    <p className="t-caption">
                      {t(m.evFeedLine, { volume: m3Compact(del), land: haUnit(vol(w.area_ha)), events: t(m.evEventsCount, { count: w.events ?? 0 }) })}
                    </p>
                    <p className="t-caption cab-feed-gap">
                      {[
                        counts.no_delivery > 0 ? t(m.evFeedNoDelivery, { count: counts.no_delivery }) : '',
                        counts.in_window > 0 ? t(m.evFeedWaiting, { count: counts.in_window }) : '',
                      ].filter(Boolean).join(' ')}
                    </p>
                  </>
                )
              ) : req !== null ? (
                <div className="wmeter">
                  <div className="wmeter__bar" role="img" aria-label={t(m.feedDelivered, { delivered: m3(del), requested: m3(req) })}>
                    <div className={`wmeter__fill ${ratio !== null && ratio >= 100 ? 'wmeter__fill--full' : ''}`} style={{ width: `${Math.min(100, ratio ?? 0)}%` }} />
                  </div>
                  <div className="wmeter__facts">
                    <span>
                      <span className="num">{m3Compact(req)}</span> → <span className="num">{m3Compact(del)}</span>
                    </span>
                    <span>
                      {t(m.feedCoverage, { value: pct(ratio, 1) })}
                    </span>
                  </div>
                </div>
              ) : phase === 'upcoming' ? (
                <p className="t-caption">{t(m.feedUpcoming)}</p>
              ) : (
                <p className="t-caption cab-feed-gap">
                  {[
                    counts.requested_not_delivered > 0 ? t(m.feedPending, { count: counts.requested_not_delivered }) : '',
                    counts.no_request > 0 ? t(m.feedNoRequest, { count: counts.no_request }) : '',
                    counts.in_window > 0 ? t(m.feedInWindow, { count: counts.in_window }) : '',
                  ].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ----------------------------------------------------------- деталь */

function Detail({ w, day, scope, linkSearch, multiFarm, events }: { w: Watering; day: string; scope: WateringViewProps['scope']; linkSearch: WateringViewProps['linkSearch']; multiFarm: boolean; events: boolean }) {
  const t = useT();
  const [only, setOnly] = useState<Set<ContourWaterState>>(new Set());
  const [channel, setChannel] = useState<string | null>(null);
  const name = wateringTitle(w.name);
  const badge = phaseBadge(w, day);
  const req = vol(w.requested_m3);
  const del = vol(w.delivered_m3);
  const counts = stateCounts(w);
  const short = vol(w.short_m3);
  const channels = useMemo(() => channelRowsOf(w), [w]);
  const channelRow = channel ? channels.find((c) => c.key === channel) ?? null : null;
  const rows = useMemo(
    () =>
      w.contours.filter((c) => {
        const s = stateOf(c);
        if (only.size && (!s || !only.has(s))) return false;
        if (channelRow && !channelRow.contourIds.includes(String(c.contour_id))) return false;
        return true;
      }),
    [w, only, channelRow],
  );
  const layer = useMemo(() => wateringLayer(w.contours, name.title, events), [w, name.title, events]);
  const ids = useMemo(() => w.contours.map((c) => String(c.contour_id)), [w]);
  const shownIds = useMemo(() => rows.map((c) => String(c.contour_id)), [rows]);
  const byId = useMemo(() => new Map(w.contours.map((c) => [String(c.contour_id), c])), [w]);

  const filters: ActiveFilterItem[] = [
    ...[...only].map((s) => ({ key: `s-${s}`, text: waterStateMeta(s).label, onClear: () => setOnly(toggled(only, s)) })),
    ...(channelRow ? [{ key: 'ch', text: t(m.filterChannel, { label: channelRow.label }), onClear: () => setChannel(null) }] : []),
  ];

  const columns: Column<WateringContour>[] = [
    {
      key: 'no',
      header: t(m.colContour),
      render: (c) => (scope.kind === 'farm' ? <Link to={`/contours/${c.contour_id}${linkSearch({ tab: 'water' })}`}>{t(m.contourNo, { no: c.contour_no })}</Link> : t(m.contourNo, { no: c.contour_no })),
    },
    {
      key: 'state',
      header: t(m.colState),
      render: (c) => {
        const s = stateOf(c);
        return s ? (
          <span className="cab-state">
            <i style={{ background: `var(${waterStateMeta(s).swatch})` }} aria-hidden />
            {waterStateMeta(s).label}
            {c.unplanned ? <small> · {t(m.unplanned)}</small> : null}
          </span>
        ) : (
          c.state
        );
      },
    },
    ...(events
      ? [
          { key: 'del', header: t(m.colDelivered), unit: t(UNIT_LABELS.m3), numeric: true, render: (c: WateringContour) => (vol(c.delivered_m3) === null ? '—' : num(Math.round(vol(c.delivered_m3)!))) },
          { key: 'land', header: t(m.evColLand), unit: t(UNIT_LABELS.ha), numeric: true, render: (c: WateringContour) => (vol(c.area_ha) === null ? '—' : ha(vol(c.area_ha))) },
          { key: 'ev', header: t(m.evColEvents), numeric: true, render: (c: WateringContour) => num(c.events ?? 0) },
        ]
      : [
          { key: 'req', header: t(m.colRequested), unit: t(UNIT_LABELS.m3), numeric: true, render: (c: WateringContour) => (vol(c.requested_m3) === null ? (c.pending_lines ? t(m.pendingCell, { value: num(vol(c.pending_requested_m3)) }) : '—') : num(Math.round(vol(c.requested_m3)!))) },
          { key: 'del', header: t(m.colDelivered), unit: t(UNIT_LABELS.m3), numeric: true, render: (c: WateringContour) => (vol(c.delivered_m3) === null ? '—' : num(Math.round(vol(c.delivered_m3)!))) },
          { key: 'cov', header: t(m.colCoverage), numeric: true, render: (c: WateringContour) => pct(c.coverage_pct ?? null, 1) },
          { key: 'short', header: t(m.colShort), unit: t(UNIT_LABELS.m3), numeric: true, render: (c: WateringContour) => (vol(c.short_m3) === null ? '—' : num(Math.round(vol(c.short_m3)!))) },
        ]),
    { key: 'ch', header: t(m.colChannel), render: (c) => c.channels.join(', ') || '—' },
  ];

  // Фильтр легенды — панель управления карты: в развёрнутой карте он стоит
  // её боковой панелью (журнал №1390).
  const legendFilter = (
    <LegendFilter
      label={t(m.legendLabel)}
      rows={WATER_STATE_ORDER.filter((s) => counts[s] > 0 || (!events && (s === 'requested_not_delivered' || s === 'no_request'))).map((s) => ({
        key: s,
        label: waterStateMeta(s).label,
        note: waterStateMeta(s, events).note,
        swatch: waterStateMeta(s).swatch,
        count: counts[s],
      }))}
      selected={only}
      onToggle={(s) => setOnly(toggled(only, s))}
      unit={(n) => t(m.contoursUnit, { count: n })}
      foot={events ? t(m.evLegendFoot) : t(m.legendFoot)}
    />
  );

  return (
    <div className="wv-detail" data-guide="water-detail">
      <div className="wv-detail__head">
        <h3 className="wv-detail__title">{name.title}</h3>
        <Badge tone={badge.tone}>{badge.text}</Badge>
        <span className="wv-card__win">
          {windowLabel(w.window_from, w.window_to)}
          {name.note ? ` · ${name.note}` : ''}
          {multiFarm ? ` · ${w.actor.name ?? t(m.farmFallback, { id: w.actor.id })}` : ''}
        </span>
      </div>
      <div className="wstats wv-stats">
        {events ? (
          <>
            <div className="wstat">
              <span className="wstat__key">{t(m.evStatDelivered)}</span>
              <span className="wstat__val">{m3Compact(del)}</span>
              <span className="wstat__note">{t(m.evStatDeliveredNote)}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.evStatLand)}</span>
              <span className="wstat__val">{vol(w.area_ha) === null ? t(m.kpiNoData) : haUnit(vol(w.area_ha))}</span>
              <span className="wstat__note">{t(m.evStatLandNote)}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.evStatEvents)}</span>
              <span className="wstat__val">{num(w.events ?? 0)}</span>
              <span className="wstat__note">{t(m.evStatEventsNote, { channels: num(channels.length) })}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.statContours)}</span>
              <span className="wstat__val">
                {num(counts.delivered + counts.partial + counts.unmeasured)} <small>{t(m.statContoursOf, { total: num(w.contours.length) })}</small>
              </span>
              <span className="wstat__note">{t(m.evStatContoursNote, { noDelivery: num(counts.no_delivery), waiting: num(counts.in_window) })}</span>
            </div>
          </>
        ) : (
          <>
        <div className="wstat">
          <span className="wstat__key">{t(m.statRequested)}</span>
          <span className="wstat__val">{m3Compact(req)}</span>
          <span className="wstat__note">{t(m.statRequestedNote)}</span>
        </div>
        <div className="wstat">
          <span className="wstat__key">{t(m.statDelivered)}</span>
          <span className="wstat__val">{m3Compact(del)}</span>
          <span className="wstat__note">{t(m.statDeliveredNote, { value: pct(w.coverage_pct ?? null, 1) })}</span>
        </div>
        <div className="wstat">
          <span className="wstat__key">{t(m.statShort)}</span>
          <span className="wstat__val">{short === null ? t(m.kpiNoData) : m3Compact(short)}</span>
          <span className="wstat__note">{t(m.statShortNote)}</span>
        </div>
        <div className="wstat">
          <span className="wstat__key">{t(m.statContours)}</span>
          <span className="wstat__val">
            {num(counts.delivered + counts.partial + counts.unmeasured)} <small>{t(m.statContoursOf, { total: num(w.contours.length) })}</small>
          </span>
          <span className="wstat__note">
            {t(m.statContoursNote, { pending: num(counts.requested_not_delivered), noRequest: num(counts.no_request) })}
          </span>
        </div>
          </>
        )}
      </div>
      <DoneSplit size="sm" tone="water" counts={{ total: w.obligations, reported: w.reported, inProgress: w.in_progress }} denominator={m.obligationsUnit} windowNote={wateringPhase(w, day) === 'active' ? t(m.windowNote) : undefined} />
      <ActiveFilter items={filters} onReset={() => { setOnly(new Set()); setChannel(null); }} />
      <div className="cab-mapgrid cab-mapgrid--below">
        <AgroMap
          colorBy="values"
          mode="frame"
          height={420}
          legend="collapsed"
          panel={{ title: t(m.legendLabel), content: legendFilter }}
          data={{ values: layer }}
          filterIds={shownIds.length ? shownIds : ids}
          focusIds={shownIds.length ? shownIds : ids}
          caption={scope.kind === 'farm' ? t(m.mapCaptionFarm, { title: name.title }) : t(m.mapCaptionContour, { title: name.title })}
          cardExtra={(c) => {
            const row = byId.get(c.id);
            if (!row) return null;
            const s = stateOf(row);
            return (
              <dl className="wv-cardx">
                <dt>{name.title}</dt>
                <dd>
                  {events
                    ? t(m.evCardState, { state: s ? waterStateMeta(s).label : row.state, delivered: m3(vol(row.delivered_m3)), land: haUnit(vol(row.area_ha)) })
                    : row.pending_lines
                    ? t(m.cardStatePending, { state: s ? waterStateMeta(s).label : row.state, requested: m3(vol(row.requested_m3)), delivered: m3(vol(row.delivered_m3)), pending: m3(vol(row.pending_requested_m3)) })
                    : t(m.cardState, { state: s ? waterStateMeta(s).label : row.state, requested: m3(vol(row.requested_m3)), delivered: m3(vol(row.delivered_m3)) })}
                </dd>
                <dt>{t(m.cardWater)}</dt>
                <dd>
                  <Link to={`/contours/${row.contour_id}${linkSearch({ tab: 'water' })}`}>{t(m.cardAllWaterings)}</Link>
                </dd>
              </dl>
            );
          }}
        />
        {legendFilter}
      </div>
      {channels.length > 0 ? (
        <div className="wv-section">
          <h4 className="wv-h">{t(m.channelsTitle)}</h4>
          <ChannelList rows={channels} selected={channel} onSelect={setChannel} events={events} />
        </div>
      ) : null}
      <div className="wv-section">
        <h4 className="wv-h">{t(m.tableTitle)}</h4>
        <DataTable columns={columns} rows={rows} getKey={(c) => `${w.actor.id}-${c.contour_id}`} denominator={m.contoursUnit} limit={12} empty={{ title: t(m.tableEmptyTitle), reason: t(m.tableEmptyReason) }} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ график */

function WateringChart({ list, events }: { list: Watering[]; events: boolean }) {
  const t = useT();
  const CHART = events ? eventsChartConfig() : chartConfig();
  const data = list.map((w) => ({
    // i18n-ignore: сокращение применяется к русскому названию полива из данных сервера
    label: wateringTitle(w.name).title.replace('Влагозарядковый полив', 'Влагозаряд.'),
    requested: vol(w.requested_m3) ?? 0,
    delivered: vol(w.delivered_m3) ?? 0,
  }));
  const ticks = niceTicks(Math.max(0, ...data.flatMap((d) => [d.requested, d.delivered])));
  return (
    <>
      <ChartContainer config={CHART} height={220} label={events ? t(m.evChartAria) : t(m.chartAria)}>
        <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barGap={2} barCategoryGap="24%">
          <CartesianGrid vertical={false} strokeDasharray="3 4" />
          <XAxis dataKey="label" interval={0} tickLine={false} axisLine={false} />
          <YAxis width={56} ticks={ticks} domain={[0, ticks.at(-1)!]} tickLine={false} axisLine={false} tickFormatter={(v: number) => m3Compact(v).replace(new RegExp(`[\\s\\u00a0]+${t(UNIT_LABELS.m3)}$`), '')} />
          <Tooltip cursor={{ fill: 'var(--data-grid)', fillOpacity: 0.5 }} content={(p) => <ChartTooltipContent {...(p as object)} config={CHART} />} />
          {events ? null : <Bar dataKey="requested" fill="var(--color-requested)" radius={[3, 3, 0, 0]} isAnimationActive={false} />}
          <Bar dataKey="delivered" fill="var(--color-delivered)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ChartContainer>
      <ChartLegend config={CHART} />
      <p className="cab-why">{events ? t(m.evChartNote) : t(m.chartNote)}</p>
    </>
  );
}
